import { useState } from "react";
import { Modal } from "./Modal";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Loader2, ShieldCheck, FileLock2 } from "lucide-react";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

// Gated lead-capture for the University Subscription Tiers fee schedule / SLA terms.
// Posts to /transmittal/request, which generates the fee schedule PDF server-side and
// emails it directly to the requester (plus an internal admin notification).
export const ExecutiveTransmittalModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", organization: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const closeAll = () => {
    onClose();
    setSent(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/transmittal/request", form);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={closeAll} testId="executive-transmittal-modal">
      <div className="p-8 md:p-10">
        {sent ? (
          <div className="text-center py-4" data-testid="transmittal-confirmation">
            <ShieldCheck size={32} className="text-[#D4AF37] mx-auto" />
            <h2 className="mt-4 font-serif text-2xl text-[#0B1B2B]">Request Received</h2>
            <p className="mt-3 text-sm text-[#64748B] leading-relaxed">
              Your Executive Transmittal — the full Subscription Tier fee schedule, including per-tier pricing and
              placement-capacity SLA terms — has been sent to{" "}
              <span className="font-semibold text-[#0B1B2B]">{form.email}</span>. A member of our team will follow up
              shortly.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Executive Transmittal</p>
            <h2 className="mt-2 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight">
              Request the Full Fee Schedule
            </h2>
            <p className="mt-4 text-sm text-[#64748B] leading-relaxed">
              Per-tier pricing and SLA terms are shared directly with verified institutional contacts rather than
              published on the public site.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input className={inputCls} placeholder="Full Name" required value={form.name} onChange={set("name")} data-testid="transmittal-name-input" />
              <input className={inputCls} placeholder="Institution" required value={form.organization} onChange={set("organization")} data-testid="transmittal-org-input" />
              <input className={inputCls} type="email" placeholder="Work Email" required pattern="[^@\s]+@[^@\s]+\.[^@\s]{2,}" title="Enter a valid institutional email address" value={form.email} onChange={set("email")} data-testid="transmittal-email-input" />
              {error && <p className="text-sm text-red-600" data-testid="transmittal-error">{error}</p>}
              <button type="submit" disabled={busy} className="w-full bg-[#0B1B2B] text-white font-semibold py-3.5 rounded-xl hover:bg-[#152a40] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="transmittal-submit-button">
                {busy && <Loader2 size={16} className="animate-spin" />}
                <FileLock2 size={15} />
                Send Me the Fee Schedule
              </button>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};
